import { OrgContext } from "contexts/OrgContext";
import React, { useContext, useEffect } from "react";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import useQueryParam from "useQueryParams";
import CreditsBillingPage from "./CreditBillingPage";
import ApplicationPageLayout from "components/ApplicationShell/ApplicationPageLayout";
import {
  isApplicationShellRedesigned,
  useApplicationShellPreference,
} from "components/ApplicationShell/ApplicationShellPreference";
import { getOrganizationSettingsTabRedirectHref } from "components/OrgSettings/settingsTabs";
import BillingSupportContactLink from "./BillingSupportContactLink";
import { UserContext } from "contexts/UserContext";
import { isOrgAdminRole } from "utils/roleUtils";
import InlineNotification from "contexts/Notifications/InlineNotifications";

interface ParamTypes {
  orgId: string;
}

interface LocationState {
  segment?: string;
}

export const OrgBillingPageRedirect: React.FC = () => {
  const router = useHistory();
  const location = useLocation();
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const [withCoupon] = useQueryParam("coupon", "");
  const [openAddCredits] = useQueryParam("openAddCredits", "false");
  const shellPreference = useApplicationShellPreference();
  const useOrganizationSettingsTabs =
    isApplicationShellRedesigned(shellPreference);

  useEffect(() => {
    if (
      orgContext.isLoading ||
      orgContext.isOrgAccessLoading ||
      orgContext.isOrgAccessError ||
      !userContext.me ||
      !orgContext.activeOrgId
    ) {
      return;
    }

    if (useOrganizationSettingsTabs) {
      const redirectHref = getOrganizationSettingsTabRedirectHref({
        activeOrgId: orgContext.activeOrgId,
        tab: "billing",
        search: location.search,
      });

      if (redirectHref) router.replace(redirectHref);
      return;
    }

    const params = new URLSearchParams();
    if (withCoupon) params.set("coupon", withCoupon);
    if (openAddCredits) params.set("openAddCredits", openAddCredits);
    const queryString = params.toString();
    router.push(
      `/org/${orgContext.activeOrgId}/billing${
        queryString ? `?${queryString}` : ""
      }`,
    );
  }, [
    location.search,
    openAddCredits,
    orgContext.activeOrgId,
    orgContext.isLoading,
    orgContext.isOrgAccessError,
    orgContext.isOrgAccessLoading,
    router,
    userContext.me,
    useOrganizationSettingsTabs,
    withCoupon,
  ]);

  return <></>;
};

interface OrgBillingPageProps {
  embedded?: boolean;
}

const OrgBillingPage: React.FC<OrgBillingPageProps> = ({
  embedded = false,
}) => {
  const { orgId } = useParams<ParamTypes>();
  const location = useLocation<LocationState>();
  const { state } = location;
  const orgContext = useContext(OrgContext);
  const billingRef = React.useRef<null | HTMLDivElement>(null);
  const shellPreference = useApplicationShellPreference();
  const useOrganizationSettingsTabs =
    isApplicationShellRedesigned(shellPreference);
  const canViewBilling = isOrgAdminRole(orgContext.activeOrgRole);

  useEffect(() => {
    document.title = "Organization Billing | Brev.dev";
    if (state?.segment === "billing") {
      billingRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [state?.segment]);

  const renderBillingContent = () => {
    if (canViewBilling) {
      return <CreditsBillingPage embedded={embedded} />;
    }

    return (
      <InlineNotification
        show={true}
        severity="warning"
        autoClose={false}
        hideCloseButton={true}
        className="!bg-amber-400/20 border border-amber-400/50"
        text="Billing is available to organization admins and owners. Contact an organization admin for help with billing."
      />
    );
  };

  if (orgContext.isOrgAccessLoading || orgContext.isOrgAccessError) {
    return <></>;
  }

  if (useOrganizationSettingsTabs && !embedded) {
    const redirectHref = getOrganizationSettingsTabRedirectHref({
      activeOrgId: orgContext.activeOrgId,
      routeOrgId: orgId,
      tab: "billing",
      search: location.search,
    });

    if (redirectHref) {
      return <Redirect to={redirectHref} />;
    }
  }

  if (orgContext.activeOrg) {
    const content = (
      <div className={embedded ? "flex flex-col pt-1" : "px-4 sm:px-6 lg:px-8"}>
        <div className="flex flex-col">
          {!embedded && (
            <div className="flex flex-row justify-between items-end">
              <div className="flex flex-col">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Billing
                </h1>
                <p className="mt-2 text-sm text-gray-700 dark:text-secondary">
                  Manage payment methods, credits, and monitor your usage
                </p>
              </div>
              {canViewBilling && (
                <BillingSupportContactLink className="ml-4 shrink-0" />
              )}
            </div>
          )}
          <div className={embedded ? "" : "mt-6"}>{renderBillingContent()}</div>
        </div>
      </div>
    );

    if (embedded) return content;

    return (
      <ApplicationPageLayout className="py-10 sm:px-6 lg:px-8">
        {content}
      </ApplicationPageLayout>
    );
  }
  return <div />;
};

export default OrgBillingPage;
